import React, { Component } from "react";
import { Grid, Row, Col, FormGroup, ControlLabel, FormControl } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";

import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";
import SeatMap from "../../components/SeatSelection/SeatMap";
import SeatLegend from "../../components/SeatSelection/SeatLegend";
import { getBusLayout } from "../../components/SeatSelection/seatConfig";
import { scheduleService } from "../../services/schedule.services";
import { seatBlockService } from "../../services/seat-block.services";
import getSessionId from "../../helpers/sessionId";
import config from "config";
import "../../components/SeatSelection/SeatSelection.css";

function titleCase(str) {
  if (!str) return "";
  return String(str).replace(/\b\w/g, c => c.toUpperCase());
}

function formatTime(t) {
  if (!t) return "";
  const m = moment(t, ["HH:mm:ss", "HH:mm", moment.ISO_8601]);
  return m.isValid() ? m.format("h:mm A") : t;
}

function queryStringToJSON(param) {
  if (!param) return {};
  const pairs = param.slice(1).split("&");
  const result = {};
  pairs.forEach(p => {
    const [k, v] = p.split("=");
    if (k) result[k] = decodeURIComponent(v || "");
  });
  return result;
}

class AddSeatBlock extends Component {
  constructor(props) {
    super(props);
    this.state = {
      takenSeats: [],
      tempTakenSeats: [],
      blockedSeats: [],
      busType: null,
      seatInfoLoading: false,
      // Two parallel selection lists: clicking an Available seat queues it for
      // blocking; clicking a Blocked seat queues it for unblocking.
      selectedToBlock: [],
      selectedToUnblock: [],
      type: "maintenance",
      notes: "",
      submitting: false,
      submittingUnblock: false,
      refreshing: false,
      error: null,
      success: null,
      warning: null,
      tripBlocks: [],
      logs: [],
      logsTotal: 0,
      logsPage: 0,
      logsPerPage: 10,
      logsLoading: false,
    };
    // Track which seats this admin session has actively held on the backend
    // (so customers can't grab them while the admin is composing the block).
    // Only seats that started as Available end up here — customer-already-held
    // seats are queued locally and overridden at createBlock time.
    this._adminHeldSeats = new Set();

    this.fetchSeatInfo = this.fetchSeatInfo.bind(this);
    this.fetchTripBlocks = this.fetchTripBlocks.bind(this);
    this.fetchLogs = this.fetchLogs.bind(this);
    this.handleSeatSelect = this.handleSeatSelect.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlockSubmit = this.handleBlockSubmit.bind(this);
    this.handleUnblockSelected = this.handleUnblockSelected.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
    this._handleBeforeUnload = this._handleBeforeUnload.bind(this);
    this._handleWindowFocus = this._handleWindowFocus.bind(this);
  }

  componentDidMount() {
    this.fetchSeatInfo();
    this.fetchTripBlocks();
    this.fetchLogs();
    window.addEventListener('beforeunload', this._handleBeforeUnload);
    window.addEventListener('focus', this._handleWindowFocus);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this._handleBeforeUnload);
    window.removeEventListener('focus', this._handleWindowFocus);
    // Release any admin-held seats so they don't sit around for 10 minutes.
    const sessionId = getSessionId();
    scheduleService.releaseAllHolds(sessionId)
      .catch(err => { console.error('[AddSeatBlock releaseAllHolds]', err); });
    this._adminHeldSeats.clear();
  }

  _handleWindowFocus() {
    // Catch admin returning from another tab/window. State may have shifted
    // (customers released holds, other admins blocked seats). Cheap silent
    // refresh — no auto-poll, no jitter while they're working.
    if (this.state.refreshing || this.state.seatInfoLoading) return;
    this.handleRefresh();
  }

  _handleBeforeUnload() {
    // Sync XHR — best-effort hold release on tab close
    const sessionId = getSessionId();
    const url = `${config.apiUrl}/schedule/seats/hold/session`;
    const xhr = new XMLHttpRequest();
    xhr.open('DELETE', url, false);
    xhr.setRequestHeader('Content-Type', 'application/json');
    try { xhr.send(JSON.stringify({ session_id: sessionId })); } catch (_e) { /* noop */ }
  }

  getTripParams() {
    const search = queryStringToJSON(this.props.location.search);
    return {
      schedule_bus: search.schedule_bus,
      slot: search.date,
      time: search.time,
      departCity: search.departCity,
      arriveCity: search.arriveCity,
      busType: search.bus_type,
      letter: search.letter,
    };
  }

  fetchSeatInfo() {
    const { schedule_bus, slot, departCity, arriveCity } = this.getTripParams();
    if (!schedule_bus || !slot) {
      this.setState({ error: "Missing trip context. Open this page from the trip search results." });
      return Promise.resolve();
    }
    this.setState({ seatInfoLoading: true, error: null });
    // Pass admin's session_id so this admin's own holds are excluded from
    // `temporaryTakenSeats` (they appear as plain selectedToBlock orange instead).
    const sessionId = getSessionId();
    return scheduleService.getSeatInfo(schedule_bus, slot, sessionId, departCity, arriveCity)
      .then(data => {
        this.setState({
          takenSeats: data.takenSeats || [],
          tempTakenSeats: data.temporaryTakenSeats || [],
          blockedSeats: data.blockedSeats || [],
          busType: data.busType || this.getTripParams().busType || "Standard",
          seatInfoLoading: false,
        });
      })
      .catch(err => {
        const message = (err && err.error) || "Failed to load seat information";
        this.setState({ error: message, seatInfoLoading: false });
      });
  }

  fetchTripBlocks() {
    // Used by the bulk-unblock flow to map selected seat numbers → block IDs.
    const { schedule_bus, slot } = this.getTripParams();
    if (!schedule_bus || !slot) return Promise.resolve();
    return seatBlockService.list({ schedule_bus, slot, limit: 200, offset: 0 })
      .then(data => {
        this.setState({ tripBlocks: data.blocks || [] });
      })
      .catch(err => {
        const message = (err && err.error) || "Failed to load existing blocks";
        this.setState({ error: message });
      });
  }

  fetchLogs(page) {
    const { schedule_bus, slot } = this.getTripParams();
    if (!schedule_bus || !slot) return Promise.resolve();
    const { logsPerPage } = this.state;
    const currentPage = page != null ? page : this.state.logsPage;
    this.setState({ logsLoading: true });
    return seatBlockService.getLogs({
      schedule_bus,
      slot,
      limit: logsPerPage,
      offset: currentPage * logsPerPage,
    })
      .then(data => {
        this.setState({
          logs: data.logs || [],
          logsTotal: data.total || 0,
          logsPage: currentPage,
          logsLoading: false,
        });
      })
      .catch(() => {
        this.setState({ logsLoading: false });
      });
  }

  handleRefresh() {
    this.setState({ refreshing: true, warning: null });
    Promise.all([this.fetchSeatInfo(), this.fetchTripBlocks()]).then(() => {
      // Prune stale selections after a refresh:
      //   - selectedToBlock: drop any seat that's been BOOKED (not blockable) or
      //     ADMIN-BLOCKED (already done). Customer-held seats are still valid
      //     block targets — admin block overrides them.
      //   - selectedToUnblock: drop any seat that's no longer blocked.
      const { selectedToBlock, selectedToUnblock, takenSeats, blockedSeats } = this.state;
      const cantBlock = new Set([...takenSeats, ...blockedSeats]);
      const blockedSet = new Set(blockedSeats);

      const prunedBlock = selectedToBlock.filter(s => !cantBlock.has(s));
      const prunedUnblock = selectedToUnblock.filter(s => blockedSet.has(s));

      const droppedFromBlock = selectedToBlock.length - prunedBlock.length;
      const droppedFromUnblock = selectedToUnblock.length - prunedUnblock.length;
      const messages = [];
      if (droppedFromBlock > 0) {
        messages.push(`${droppedFromBlock} seat(s) queued for blocking are now booked or already blocked`);
      }
      if (droppedFromUnblock > 0) {
        messages.push(`${droppedFromUnblock} seat(s) queued for unblocking are no longer blocked`);
      }

      this.setState({
        refreshing: false,
        selectedToBlock: prunedBlock,
        selectedToUnblock: prunedUnblock,
        warning: messages.length ? messages.join('; ') + '. Selection updated.' : null,
      });
    });
  }

  handleSeatSelect(seatId) {
    // Route the click based on current seat state:
    //   - FCFS row           → reject (FCFS seats are operational; blocking
    //                          them is a no-op since nothing pre-assigns them)
    //   - Blocked            → toggle in selectedToUnblock (no backend hold)
    //   - Customer-held      → toggle in selectedToBlock locally; the createBlock
    //                          call will override the customer hold on submit
    //   - Available          → toggle in selectedToBlock AND acquire a real
    //                          backend hold so customers can't grab it while
    //                          the admin is composing the block
    const { blockedSeats, tempTakenSeats, selectedToBlock, selectedToUnblock, busType } = this.state;
    const { busType: busTypeParam } = this.getTripParams();
    const { schedule_bus, slot } = this.getTripParams();
    const sessionId = getSessionId();

    // Defense-in-depth FCFS guard (the SeatMap renders these as non-clickable
    // gray "FCFS" tiles, but the click handler still gets through with adminMode).
    const layout = getBusLayout(busType || busTypeParam || "Standard");
    const maxRow = layout && layout.maxSelectableRow;
    if (maxRow != null) {
      const rowMatch = seatId.match(/^(\d+)/);
      if (rowMatch && parseInt(rowMatch[1], 10) > maxRow) {
        return;
      }
    }

    const isBlocked = blockedSeats.indexOf(seatId) !== -1;
    const isCustomerHeld = tempTakenSeats.indexOf(seatId) !== -1;
    const alreadySelectedBlock = selectedToBlock.indexOf(seatId) !== -1;
    const alreadySelectedUnblock = selectedToUnblock.indexOf(seatId) !== -1;

    if (isBlocked) {
      this.setState({
        selectedToUnblock: alreadySelectedUnblock
          ? selectedToUnblock.filter(s => s !== seatId)
          : selectedToUnblock.concat([seatId]),
      });
      return;
    }

    if (alreadySelectedBlock) {
      // Deselecting — drop locally. If we held it on the backend, release it.
      this.setState({
        selectedToBlock: selectedToBlock.filter(s => s !== seatId),
      });
      if (this._adminHeldSeats.has(seatId)) {
        this._adminHeldSeats.delete(seatId);
        scheduleService.releaseSeat(schedule_bus, slot, seatId, sessionId)
          .catch(err => { console.error('[releaseSeat]', err); });
      }
      return;
    }

    // Optimistically queue for blocking. Show orange immediately.
    this.setState({ selectedToBlock: selectedToBlock.concat([seatId]) });

    // Customer-held seats: skip the hold call (would 409). The override
    // happens at createBlock time.
    if (isCustomerHeld) {
      return;
    }

    // Available seats: acquire a real backend hold.
    scheduleService.holdSeat(schedule_bus, slot, seatId, sessionId)
      .then(() => {
        this._adminHeldSeats.add(seatId);
      })
      .catch(err => {
        // Conflict — someone else grabbed it between render and click. Roll back
        // the optimistic add and refresh state so the seat repaints correctly.
        console.warn('[holdSeat]', seatId, err);
        this.setState(prev => ({
          selectedToBlock: prev.selectedToBlock.filter(s => s !== seatId),
          warning: `Seat ${seatId} just became unavailable. Selection updated.`,
        }));
        this.fetchSeatInfo();
      });
  }

  handleChange(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  handleBlockSubmit() {
    const { selectedToBlock, type, notes } = this.state;
    const { schedule_bus, slot } = this.getTripParams();

    if (!selectedToBlock.length) {
      this.setState({ error: "Select at least one available seat to block." });
      return;
    }

    this.setState({ submitting: true, error: null, success: null });

    seatBlockService.create({
      schedule_bus,
      slot,
      seat_numbers: selectedToBlock,
      type,
      notes: notes || null,
    })
      .then(() => {
        // Backend createBlock removes the corresponding seat_holds rows, so
        // there's nothing left to release for this batch.
        this._adminHeldSeats.clear();
        this.setState({
          submitting: false,
          success: `Blocked ${selectedToBlock.length} seat(s) successfully.`,
          selectedToBlock: [],
          notes: "",
        });
        this.fetchSeatInfo();
        this.fetchTripBlocks();
        this.fetchLogs(0);
      })
      .catch(err => {
        const message = (err && err.error) || "Failed to create seat blocks";
        this.setState({ submitting: false, error: message });
      });
  }

  handleUnblockSelected() {
    const { selectedToUnblock, tripBlocks } = this.state;
    if (!selectedToUnblock.length) return;

    // Map seat numbers → block IDs using the loaded tripBlocks.
    const idsToDelete = selectedToUnblock
      .map(seatNum => {
        const b = tripBlocks.find(tb => tb.seat_number === seatNum);
        return b ? b.id : null;
      })
      .filter(Boolean);

    if (!idsToDelete.length) {
      this.setState({ error: "Could not resolve block IDs for selected seats. Try refreshing the page." });
      return;
    }

    confirmAlert({
      title: "Unblock Seats",
      message: `Unblock ${idsToDelete.length} seat(s): ${selectedToUnblock.join(", ")}?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            this.setState({ submittingUnblock: true, error: null, success: null });
            Promise.all(idsToDelete.map(id => seatBlockService.delete(id)))
              .then(() => {
                this.setState({
                  submittingUnblock: false,
                  success: `Unblocked ${idsToDelete.length} seat(s).`,
                  selectedToUnblock: [],
                });
                this.fetchSeatInfo();
                this.fetchTripBlocks();
                this.fetchLogs(0);
              })
              .catch(err => {
                const message = (err && err.error) || "Failed to unblock one or more seats";
                this.setState({ submittingUnblock: false, error: message });
              });
          },
        },
        { label: "No", onClick: () => "" },
      ],
    });
  }

  render() {
    const { slot, time, departCity, arriveCity, busType: busTypeParam, letter } = this.getTripParams();
    const { takenSeats, tempTakenSeats, blockedSeats, selectedToBlock, selectedToUnblock,
      busType, error, success, warning, submitting, submittingUnblock, seatInfoLoading, refreshing } = this.state;

    const effectiveBusType = busType || busTypeParam || "Standard";
    const busLayout = getBusLayout(effectiveBusType);
    const route = (departCity || arriveCity)
      ? `${titleCase(departCity || "?")} → ${titleCase(arriveCity || "?")}`
      : "—";
    const busLabel = letter ? `Bus ${letter} (${effectiveBusType})` : effectiveBusType;
    // Combined selection — both block and unblock targets render as "selected" (orange)
    // on the seat map. The router (handleSeatSelect) keeps them in distinct buckets.
    const selectedSeats = selectedToBlock.concat(selectedToUnblock);

    return (
      <div className="content">
        <Grid fluid>
          <Row>
            <Col md={12}>
              <Card
                title="Block Seats"
                content={
                  <div>
                    <Row>
                      <Col md={4}>
                        <strong>Route:</strong> {route}
                      </Col>
                      <Col md={4}>
                        <strong>Date:</strong> {slot ? moment(slot).format("ddd, MMM D, YYYY") : "—"}
                        {time ? <span style={{ marginLeft: 8, color: "#717374" }}>at {formatTime(time)}</span> : null}
                      </Col>
                      <Col md={4}>
                        <strong>Bus:</strong> {busLabel}
                      </Col>
                    </Row>
                    {error && <div className="alert alert-danger" style={{ marginTop: 10 }}>{error}</div>}
                    {success && <div className="alert alert-success" style={{ marginTop: 10 }}>{success}</div>}
                    {warning && <div className="alert alert-warning" style={{ marginTop: 10 }}>{warning}</div>}

                    <Row style={{ marginTop: 20 }}>
                      <Col md={8}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <h4 style={{ margin: 0 }}>Click Seats to Block or Unblock</h4>
                          <button
                            type="button"
                            className={'btn-refresh' + (refreshing ? ' refreshing' : '')}
                            onClick={this.handleRefresh}
                            title="Refresh seat availability"
                            disabled={refreshing}
                            style={{ background: 'transparent', border: 'none', color: '#1b1e21', cursor: refreshing ? 'wait' : 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                          >
                            <svg
                              width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                              style={refreshing ? { animation: 'spin 0.8s linear infinite' } : undefined}
                            >
                              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                            </svg>
                          </button>
                        </div>
                        <p style={{ color: "#717374", fontSize: 13, marginTop: 6 }}>
                          Click an <strong>available</strong> or <strong>customer-held</strong> seat to queue it for blocking (held seats are overridden). Click a <strong>blocked</strong> seat to queue it for unblocking. <strong>Booked</strong> and <strong>FCFS</strong> seats can&apos;t be blocked (FCFS seats are managed at boarding, not pre-assigned).
                        </p>
                        {seatInfoLoading ? (
                          <p>Loading seat layout...</p>
                        ) : (
                          <div className="admin-seat-selection">
                            <div className="seat-map-container">
                              <SeatMap
                                busLayout={busLayout}
                                selectedSeats={selectedSeats}
                                takenSeats={takenSeats}
                                temporaryTakenSeats={tempTakenSeats}
                                blockedSeats={blockedSeats}
                                onSeatSelect={this.handleSeatSelect}
                                maxSelectableRow={busLayout.maxSelectableRow}
                                blockedClickable={true}
                                tempHeldClickable={true}
                                showSeatId={true}
                              />
                              <SeatLegend
                                showPricingInfo={false}
                                showFCFS={false}
                                adminMode={true}
                              />
                            </div>
                          </div>
                        )}
                      </Col>
                      <Col md={4}>
                        <h4 style={{ marginTop: 0 }}>Block</h4>
                        <FormGroup>
                          <ControlLabel>Block Type</ControlLabel>
                          <FormControl componentClass="select" name="type" value={this.state.type} onChange={this.handleChange}>
                            <option value="maintenance">Maintenance</option>
                            <option value="special_reservation">Special Reservation</option>
                            <option value="other">Other</option>
                          </FormControl>
                        </FormGroup>
                        <FormGroup>
                          <ControlLabel>Notes (optional)</ControlLabel>
                          <FormControl
                            componentClass="textarea"
                            rows={3}
                            name="notes"
                            value={this.state.notes}
                            onChange={this.handleChange}
                            placeholder="e.g., Broken seat, awaiting repair"
                          />
                        </FormGroup>
                        <FormGroup>
                          <ControlLabel>Selected to Block</ControlLabel>
                          <div>{selectedToBlock.length ? selectedToBlock.join(", ") : "None"}</div>
                        </FormGroup>
                        <Button
                          bsStyle="danger"
                          fill
                          type="button"
                          onClick={this.handleBlockSubmit}
                          disabled={submitting || !selectedToBlock.length}
                        >
                          {submitting ? "Blocking..." : `Block ${selectedToBlock.length} Seat${selectedToBlock.length === 1 ? "" : "s"}`}
                        </Button>

                        <hr style={{ margin: "20px 0" }} />

                        <h4 style={{ marginTop: 0 }}>Unblock</h4>
                        <FormGroup>
                          <ControlLabel>Selected to Unblock</ControlLabel>
                          <div>{selectedToUnblock.length ? selectedToUnblock.join(", ") : "None"}</div>
                        </FormGroup>
                        <Button
                          bsStyle="warning"
                          fill
                          type="button"
                          onClick={this.handleUnblockSelected}
                          disabled={submittingUnblock || !selectedToUnblock.length}
                        >
                          {submittingUnblock ? "Unblocking..." : `Unblock ${selectedToUnblock.length} Seat${selectedToUnblock.length === 1 ? "" : "s"}`}
                        </Button>
                      </Col>
                    </Row>

                  </div>
                }
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 20 }}>
            <Col md={12}>
              <Card
                title={`History${this.state.logsTotal > 0 ? ` (${this.state.logsTotal})` : ""}`}
                content={
                  <div>
                    {this.state.logsLoading ? (
                      <p>Loading history...</p>
                    ) : this.state.logs.length === 0 ? (
                      <p style={{ color: "#999" }}>No block/unblock activity recorded for this trip yet.</p>
                    ) : (
                      <div>
                        <table className="table table-striped table-hover" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>Seat</th>
                              <th>Action</th>
                              <th>Notes</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {this.state.logs.map(log => (
                              <tr key={log.id}>
                                <td>{log.seat_number}</td>
                                <td>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 8px",
                                      borderRadius: 4,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: "#fff",
                                      backgroundColor: log.action === "blocked" ? "#e74c3c" : "#27ae60",
                                    }}
                                  >
                                    {titleCase(log.action)}
                                  </span>
                                </td>
                                <td>{log.notes || "—"}</td>
                                <td>{moment(log.created_at).format("MMM D, YYYY h:mm A")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {this.state.logsTotal > this.state.logsPerPage && (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, padding: "0 4px" }}>
                            <span style={{ fontSize: 13, color: "#717374" }}>
                              Page {this.state.logsPage + 1} of {Math.ceil(this.state.logsTotal / this.state.logsPerPage)}
                            </span>
                            <div>
                              <button
                                type="button"
                                className="btn btn-sm btn-default"
                                disabled={this.state.logsPage === 0 || this.state.logsLoading}
                                onClick={() => this.fetchLogs(this.state.logsPage - 1)}
                                style={{ marginRight: 6 }}
                              >
                                Previous
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-default"
                                disabled={(this.state.logsPage + 1) * this.state.logsPerPage >= this.state.logsTotal || this.state.logsLoading}
                                onClick={() => this.fetchLogs(this.state.logsPage + 1)}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                }
              />
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

export default AddSeatBlock;
